import { DEMO_IDS, DEMO_IMEI, DEMO_MQTT_USER, DEMO_MQTT_PASSWORD, DEMO_MQTT_TOPIC_PUB, DEMO_MQTT_TOPIC_SUB, mqttLastReport } from './fixtures/mqttPacket'
import { seedFabricantes, seedModelos, seedOperadoras, seedTimezones, seedTipoAcessos, seedMonitoradoTipos, seedPermissoesCliente, seedModulosAcesso, seedConfigCores, seedConfigSistema } from './fixtures/seedLookups'
import { buildDemoSupervisorio, buildSupervisorioReporte, buildSupervisorioHistorico } from './fixtures/supervisorio'
import {
    DEMO_SANDBOX_PARAM_DEFINITIONS,
    getLatestSandboxValues,
    buildStatusReportTableRows,
    buildChartSeries,
    buildConsumoSeries,
} from './fixtures/sandboxReadings'

const STORE_KEY = 'hdv_demo_store'

const emptyState = () => ({
    nextCampoId: DEMO_IDS.campoTemp,
    equipamento: null,
    campos: [],
    perfil: null,
    cliente: null,
    monitorado: null,
    monitoradoParametros: [],
    supervisorio: null,
})

const parseBody = (data) => {
    if (!data) return {}

    if (typeof FormData !== 'undefined' && data instanceof FormData) {
        const formFields = {}
        data.forEach((value, key) => {
            if (Object.prototype.hasOwnProperty.call(formFields, key)) {
                const current = formFields[key]
                formFields[key] = Array.isArray(current)
                    ? [...current, value]
                    : [current, value]
            } else {
                formFields[key] = value
            }
        })
        return formFields
    }

    if (typeof data === 'string') {
        try {
            return JSON.parse(data)
        } catch (error) {
            return { raw: data }
        }
    }

    return data
}

const paginate = (results) => ({
    count: Array.isArray(results) ? results.length : 0,
    next: null,
    previous: null,
    results: Array.isArray(results) ? results : [],
})

class DemoStore {
    constructor() {
        this.state = emptyState()
    }

    load() {
        try {
            const raw = sessionStorage.getItem(STORE_KEY)
            if (raw) {
                this.state = { ...emptyState(), ...JSON.parse(raw) }
            }
        } catch (error) {
            this.state = emptyState()
        }

        if (!Array.isArray(this.state.campos)) this.state.campos = []
        if (!Array.isArray(this.state.monitoradoParametros)) {
            this.state.monitoradoParametros = []
        }
    }

    persist() {
        try {
            sessionStorage.setItem(STORE_KEY, JSON.stringify(this.state))
        } catch (error) {}
    }

    clear() {
        this.state = emptyState()
        try {
            sessionStorage.removeItem(STORE_KEY)
        } catch (error) {}
    }

    seed() {
        this.state = emptyState()
        this.state.supervisorio = buildDemoSupervisorio()
        this.persist()
    }

    seedThroughStage(stage) {
        if (!stage || stage === 'none') return this.state

        this.load()

        if (stage === 'equipamento') {
            if (!this.state.equipamento) this.createEquipamento()
            this.persist()
            return this.state
        }

        const levelByStage = {
            param: 1,
            perfil: 2,
            cliente: 3,
            monitorado: 4,
            'params-edit': 5,
            'monitorado-demo': 6,
            status: 6,
            supervisorio: 6,
        }
        const level = levelByStage[stage] ?? 0
        if (level < 1) return this.state

        if (!this.state.equipamento) this.createEquipamento()

        if (level >= 1 && !this.state.campos.length) {
            this.createCampo({
                identificacao: 'temperatura',
                descricao: 'Temperatura',
                formula: '[temperatura]',
                tipo_grafico: 'linha',
                unidade: '°C',
            })
        }

        if (level >= 2 && !this.state.perfil) {
            this.createPerfil({ nome: 'Perfil Demonstração' })
        }

        if (level >= 3 && !this.state.cliente) {
            if (!this.state.perfil) {
                this.createPerfil({ nome: 'Perfil Demonstração' })
            }
            this.createCliente({ nome_fantasia: 'Cliente Demonstração' })
        }

        if (level >= 4) {
            if (!this.state.monitorado) {
                this.createMonitorado({ nome: 'Monitorado Demonstração' })
            } else if (!this.state.monitorado.id) {
                this.state.monitorado.id = DEMO_IDS.monitorado
                this.persist()
            }
        }

        if (level >= 5) {
            this.ensureSandboxDataset()
        }

        this.persist()
        return this.state
    }

    ensureSandboxDataset() {
        if (!this.state.equipamento) this.createEquipamento()

        DEMO_SANDBOX_PARAM_DEFINITIONS.forEach((definition) => {
            const alreadyExists = this.state.campos.some(
                (campo) => campo.identificacao === definition.identificacao
            )
            if (!alreadyExists) {
                this.createCampo({
                    equipamento: DEMO_IDS.equipamento,
                    ...definition,
                })
            }
        })

        if (!this.state.perfil) {
            this.createPerfil({ nome: 'Perfil Demonstração' })
        }
        if (!this.state.cliente) {
            this.createCliente({ nome_fantasia: 'Cliente Demonstração' })
        }
        if (!this.state.monitorado) {
            this.createMonitorado({ nome: 'Monitorado Demonstração' })
        } else if (!this.state.monitorado.id) {
            this.state.monitorado.id = DEMO_IDS.monitorado
        }

        this.state.campos.forEach((campo) => {
            const alreadyLinked = this.state.monitoradoParametros.some(
                (link) =>
                    Number(link.campo_id) === Number(campo.id) ||
                    Number(link.campo?.id) === Number(campo.id)
            )
            if (!alreadyLinked) {
                this.linkParametro({
                    monitorado_id: DEMO_IDS.monitorado,
                    campo_id: campo.id,
                })
            }
        })

        this.ensureSupervisorio()
        this.state.monitorado.ultima_comunicacao = Math.floor(Date.now() / 1000)
        this.persist()
        return this.state
    }

    createEquipamento(body = {}) {
        const equipamento = {
            id: DEMO_IDS.equipamento,
            imei: body.imei || DEMO_IMEI,
            fabricante: { id: Number(body.fabricante) || DEMO_IDS.fabricante, nome: 'Afira Demo' },
            modelo: { id: Number(body.modelo) || DEMO_IDS.modelo, modelo: 'Sensor MQTT Demo' },
            operadora: {
                id: Number(body.operadora) || DEMO_IDS.operadora,
                nome: 'Operadora Demo',
                possui_chip: true,
            },
            sim_card: body.sim_card || 'DEMO-SIM',
            status: body.status || 'ativo',
            tipo_comunicacao: 'mqtt',
            gateway: false,
            topico_personalizado: body.topico_personalizado !== undefined
                ? Boolean(body.topico_personalizado)
                : true,
            topico_recebe: body.topico_recebe || DEMO_MQTT_TOPIC_SUB,
            topico_publica: body.topico_publica || DEMO_MQTT_TOPIC_PUB,
            usuario_mqtt: DEMO_MQTT_USER,
            senha_mqtt: DEMO_MQTT_PASSWORD,
            pub_extras: body.pub_extras || [],
            sub_extras: body.sub_extras || [],
            acesso: {
                id: DEMO_IDS.tipoAcesso,
                acesso_tipo: DEMO_IDS.tipoAcesso,
                acesso_tipo__descricao: 'Acesso Demo',
            },
            validacao: {
                timezone_equipamento: body.timezone || 'America/Sao_Paulo',
                tempo_limite_sem_comunicacao: Number(body.tempo_limite_sem_comunicacao) || 0,
                emails_notificar: body.emails_notificar || [],
                buffer_aviso_dispensado: body.buffer_aviso_dispensado === true,
            },
        }

        this.state.equipamento = equipamento
        this.persist()
        return equipamento
    }

    updateEquipamento(body = {}) {
        if (!this.state.equipamento) {
            return this.createEquipamento(body)
        }

        const current = this.state.equipamento
        this.state.equipamento = {
            ...current,
            ...body,
            id: current.id,
            tipo_comunicacao: 'mqtt',
            usuario_mqtt: current.usuario_mqtt || DEMO_MQTT_USER,
            senha_mqtt: current.senha_mqtt || DEMO_MQTT_PASSWORD,
            topico_publica: body.topico_publica || current.topico_publica || DEMO_MQTT_TOPIC_PUB,
            topico_recebe: body.topico_recebe || current.topico_recebe || DEMO_MQTT_TOPIC_SUB,
            fabricante: body.fabricante
                ? { id: Number(body.fabricante), nome: current.fabricante?.nome || 'Afira Demo' }
                : current.fabricante,
            modelo: body.modelo
                ? { id: Number(body.modelo), modelo: current.modelo?.modelo || 'Sensor MQTT Demo' }
                : current.modelo,
            operadora: body.operadora
                ? {
                    id: Number(body.operadora),
                    nome: current.operadora?.nome || 'Operadora Demo',
                    possui_chip: true,
                }
                : current.operadora,
            acesso: current.acesso,
            validacao: {
                ...current.validacao,
                timezone_equipamento: body.timezone || current.validacao?.timezone_equipamento,
                tempo_limite_sem_comunicacao:
                    body.tempo_limite_sem_comunicacao ??
                    current.validacao?.tempo_limite_sem_comunicacao,
                buffer_aviso_dispensado:
                    body.buffer_aviso_dispensado ??
                    current.validacao?.buffer_aviso_dispensado,
            },
        }
        this.persist()
        return this.state.equipamento
    }

    getHistorico() {
        if (!this.state.equipamento) {
            return { last_report: null, modelo: null }
        }

        let payload = {}
        try {
            payload = JSON.parse(mqttLastReport)
        } catch (error) {
            payload = {}
        }

        payload.timestamp = Math.floor(Date.now() / 1000)
        payload.id = payload.id || '068416104'
        payload.meta = {
            ...(payload.meta || {}),
            imei: this.state.equipamento.imei || DEMO_IMEI,
            usuario_mqtt: this.state.equipamento.usuario_mqtt || DEMO_MQTT_USER,
        }

        return {
            last_report: JSON.stringify(payload),
            modelo: this.state.equipamento?.modelo || { id: DEMO_IDS.modelo, modelo: 'Sensor MQTT Demo' },
        }
    }

    listCampos(equipamentoId) {
        const id = Number(equipamentoId)
        const results = this.state.campos.filter((campo) => {
            if (!id) return true
            return Number(campo.equipamento) === id || Number(campo.equipamento?.id) === id
        })
        return paginate(results)
    }

    createCampo(body = {}) {
        const id = this.state.nextCampoId
        this.state.nextCampoId += 1

        const campo = {
            id,
            equipamento: Number(body.equipamento) || DEMO_IDS.equipamento,
            identificacao: body.identificacao || `param_${id}`,
            descricao: body.descricao || `Parâmetro ${id}`,
            formula: body.formula || '[temperatura]',
            tipo_dado_saida: body.tipo_dado_saida || 'decimal',
            tipo_grafico: body.tipo_grafico || 'linha',
            unidade: body.unidade || '°C',
            mascara: body.mascara || '',
            decimal: body.decimal || '2',
            color: body.color || '#2f80ed',
            icone: body.icone || null,
        }

        this.state.campos.push(campo)
        this.persist()
        return campo
    }

    createPerfil(body = {}) {
        const perfil = {
            id: DEMO_IDS.perfil,
            nome: body.nome || body.descricao || 'Perfil Demonstração',
            descricao: body.descricao || body.nome || 'Perfil Demonstração',
            permissoes: Array.isArray(body.permissoes)
                ? body.permissoes.map((id) => ({ id: Number(id) }))
                : String(body.permissoes || '')
                    .split(',')
                    .filter(Boolean)
                    .map((id) => ({ id: Number(id) })),
        }
        this.state.perfil = perfil
        this.persist()
        return perfil
    }

    toClientePayload(cliente = this.state.cliente) {
        if (!cliente) return null

        const now = new Date().toISOString()
        return {
            ...cliente,
            data_criado: cliente.data_criado || now,
            data_atualizado: cliente.data_atualizado || cliente.data_criado || now,
            acessar: cliente.acessar || {
                status: false,
                icone: 'las la-sign-in-alt',
                title: 'Indisponível no modo demonstração',
            },
        }
    }

    createCliente(body = {}) {
        const perfilId = Number(body.perfil) || this.state.perfil?.id || DEMO_IDS.perfil
        const now = new Date().toISOString()
        const cliente = this.toClientePayload({
            id: DEMO_IDS.cliente,
            nome_fantasia: body.nome_fantasia || 'Cliente Demonstração',
            razao_social: body.razao_social || 'Cliente Demonstração LTDA',
            cpf_cnpj: body.cpf_cnpj || '00.000.000/0001-00',
            email: body.email || body.email_administrador || 'demo@afira.io',
            email_administrador: body.email_administrador || body.email || 'demo@afira.io',
            pessoa_tipo: body.pessoa_tipo || 'fisica',
            status: body.status || 'ativo',
            perfil: perfilId,
            perfil_nome: this.state.perfil?.nome || 'Perfil Demonstração',
            usuario: body.usuario || 'cliente.demo',
            data_criado: now,
            data_atualizado: now,
        })
        this.state.cliente = cliente
        this.persist()
        return cliente
    }

    updateCliente(body = {}) {
        if (!this.state.cliente) return this.createCliente(body)
        this.state.cliente = this.toClientePayload({
            ...this.state.cliente,
            ...body,
            id: this.state.cliente.id,
            data_atualizado: new Date().toISOString(),
        })
        this.persist()
        return this.state.cliente
    }

    createMonitorado(body = {}) {
        const monitorado = {
            id: DEMO_IDS.monitorado,
            nome: body.nome || 'Monitorado Demonstração',
            cliente: this.state.cliente
                ? {
                    id: this.state.cliente.id,
                    nome_fantasia: this.state.cliente.nome_fantasia,
                }
                : {
                    id: Number(body.cliente_id) || DEMO_IDS.cliente,
                    nome_fantasia: 'Cliente Demonstração',
                },
            tipo: {
                id: Number(body.tipo_id) || DEMO_IDS.tipoMonitorado,
                nome: 'Ponto Demo',
                icone: 'las la-tint',
                cor: '#2f80ed',
                default_cor: '#2f80ed',
            },
            latitude: body.latitude || '-23.5505',
            longitude: body.longitude || '-46.6333',
            point: body.point || body.address || 'Av. Paulista, 1000 - São Paulo, SP',
            fotos: [],
            ultima_comunicacao: Math.floor(Date.now() / 1000),
        }
        this.state.monitorado = monitorado
        this.persist()
        return monitorado
    }

    updateMonitorado(body = {}) {
        if (!this.state.monitorado) return this.createMonitorado(body)
        this.state.monitorado = {
            ...this.state.monitorado,
            ...body,
            id: this.state.monitorado.id,
            nome: body.nome || this.state.monitorado.nome,
        }
        this.persist()
        return this.state.monitorado
    }

    linkParametro(body = {}) {
        const campoId = Number(body.campo_id)
        const campo = this.state.campos.find((item) => item.id === campoId) || this.state.campos[0]
        if (!campo) return null

        const alreadyLinked = this.state.monitoradoParametros.find(
            (item) => Number(item.campo_id) === Number(campo.id)
        )
        if (alreadyLinked) return alreadyLinked

        const equipamento = this.state.equipamento || this.createEquipamento()

        const link = {
            id: 900300 + this.state.monitoradoParametros.length,
            monitorado_id: Number(body.monitorado_id) || DEMO_IDS.monitorado,
            campo_id: campo.id,
            campo: {
                ...campo,
                equipamento: {
                    id: equipamento.id,
                    imei: equipamento.imei,
                    usuario_mqtt: equipamento.usuario_mqtt,
                },
            },
        }
        this.state.monitoradoParametros.push(link)
        this.persist()
        return link
    }

    unlinkParametro(linkId) {
        const id = Number(linkId)
        this.state.monitoradoParametros = this.state.monitoradoParametros.filter(
            (link) => Number(link.id) !== id
        )
        this.persist()
        return { detail: 'Parâmetro removido' }
    }

    listMonitorados() {
        if (!this.state.monitorado) return paginate([])
        const monitorado = {
            ...this.state.monitorado,
            id: Number(this.state.monitorado.id) || DEMO_IDS.monitorado,
            nome: this.state.monitorado.nome || 'Monitorado Demonstração',
            fotos: Array.isArray(this.state.monitorado.fotos)
                ? this.state.monitorado.fotos
                : [],
            ultima_comunicacao:
                this.state.monitorado.ultima_comunicacao ||
                Math.floor(Date.now() / 1000),
        }
        this.state.monitorado = monitorado
        this.persist()
        return paginate([monitorado])
    }

    getMonitoradoStatus() {
        if (!this.state.monitorado || !this.state.equipamento || !this.state.monitoradoParametros.length) {
            this.ensureSandboxDataset()
        }

        const monitorado = this.state.monitorado
        const equipamento = this.state.equipamento
        if (!monitorado || !equipamento) return null

        const latestValues = getLatestSandboxValues()
        const linkedParametros = this.state.monitoradoParametros.length
            ? this.state.monitoradoParametros
            : this.state.campos.map((campo) => ({ campo, campo_id: campo.id }))

        const parametros = linkedParametros.map((link, index) => {
            const campo = link.campo || link
            const fieldName = campo.identificacao
            const currentValue =
                latestValues[fieldName] !== undefined
                    ? latestValues[fieldName]
                    : 0

            return {
                id: link.id || 900400 + index,
                valor_atual: currentValue,
                campo: {
                    id: campo.id,
                    identificacao: campo.identificacao,
                    descricao: campo.descricao,
                    unidade: campo.unidade || '',
                    mascara: campo.mascara || 'padrao',
                    color: campo.color || '#2f80ed',
                    tipo_dado_saida: campo.tipo_dado_saida || 'decimal',
                    tipo_grafico: campo.tipo_grafico || 'linha',
                    configuracao: '{}',
                    equipamento: {
                        id: equipamento.id,
                        imei: equipamento.imei,
                        usuario_mqtt: equipamento.usuario_mqtt,
                    },
                    icone: { icone_on: '', icone_off: '' },
                },
            }
        })

        return {
            id: monitorado.id,
            nome: monitorado.nome,
            latitude: monitorado.latitude,
            longitude: monitorado.longitude,
            condicoes: [],
            tipo: monitorado.tipo,
            fotos: [],
            automacoes: [],
            comandos: [],
            equipamentos: [
                {
                    id: equipamento.id,
                    imei: equipamento.imei,
                    usuario_mqtt: equipamento.usuario_mqtt,
                    validacao: {
                        parametros: parametros.map((parametro) => ({
                            id: parametro.campo.id,
                            identificacao: parametro.campo.identificacao,
                            descricao: parametro.campo.descricao,
                            unidade: parametro.campo.unidade,
                        })),
                        automacoes: [],
                    },
                },
            ],
            parametros,
        }
    }

    getDadosDashboard() {
        this.ensureSandboxDataset()

        const monitorado = this.state.monitorado
        const equipamento = this.state.equipamento
        if (!monitorado || !equipamento) return []

        const latestValues = getLatestSandboxValues()
        const timestamp =
            Number(latestValues.timestamp_dispositivo) ||
            Number(monitorado.ultima_comunicacao) ||
            Math.floor(Date.now() / 1000)

        const linkedParametros = this.state.monitoradoParametros.length
            ? this.state.monitoradoParametros
            : this.state.campos.map((campo) => ({ campo, campo_id: campo.id }))

        const campos = linkedParametros.map((link) => {
            const campo = link.campo || this.state.campos.find((item) => item.id === link.campo_id) || link
            const fieldName = campo.identificacao
            const currentValue =
                latestValues[fieldName] !== undefined ? latestValues[fieldName] : 0

            let iconeSerialized = null
            if (campo.icone) {
                iconeSerialized =
                    typeof campo.icone === 'string' ? campo.icone : JSON.stringify(campo.icone)
            }

            return {
                campo__identificacao: fieldName,
                campo__descricao: campo.descricao || fieldName,
                campo__unidade: campo.unidade || '',
                campo__mascara: campo.mascara || 'padrao',
                campo__icone: iconeSerialized,
                campo__equipamento: equipamento.id,
                campo__equipamento__imei: equipamento.imei,
                campo__equipamento__usuario_mqtt: equipamento.usuario_mqtt,
                ultimo_evento: {
                    timestamp_dispositivo: timestamp,
                    tempo_limite_sem_comunicacao: 0,
                    [fieldName]: currentValue,
                },
            }
        })

        const latitude = Number(monitorado.latitude)
        const longitude = Number(monitorado.longitude)

        return [
            {
                id: Number(monitorado.id) || DEMO_IDS.monitorado,
                nome: monitorado.nome || 'Monitorado Demonstração',
                latitude: Number.isFinite(latitude) ? latitude : -23.5505,
                longitude: Number.isFinite(longitude) ? longitude : -46.6333,
                tipo__icone: monitorado.tipo?.icone || 'las la-tint',
                tipo__cor: monitorado.tipo?.cor || '#2f80ed',
                tipo__default_cor: monitorado.tipo?.default_cor || '#2f80ed',
                tem_alerta: false,
                alertas: [],
                condicoes: [],
                ultimo_evento: {
                    timestamp_dispositivo: timestamp,
                },
                campos,
            },
        ]
    }

    listStatusReportes() {
        if (!this.state.campos.length) {
            this.ensureSandboxDataset()
        }

        const fieldNames = this.state.campos.map((campo) => campo.identificacao)
        if (!fieldNames.length) {
            return { count: 0, next: null, previous: null, lastKey: null, results: [] }
        }
        return buildStatusReportTableRows(fieldNames)
    }

    getLookups(resource) {
        switch (resource) {
            case 'fabricante':
                return seedFabricantes()
            case 'modelo':
                return seedModelos()
            case 'operadora':
                return seedOperadoras()
            case 'timezone':
                return seedTimezones()
            case 'tipo-acesso':
                return seedTipoAcessos()
            case 'monitorado-tipo':
                return seedMonitoradoTipos()
            case 'permissoes-cliente':
                return seedPermissoesCliente()
            case 'modulos':
                return seedModulosAcesso()
            case 'cores':
                return seedConfigCores()
            case 'sistema':
                return seedConfigSistema()
            default:
                return paginate([])
        }
    }

    chart(fieldName) {
        if (!this.state.campos.length) {
            this.ensureSandboxDataset()
        }

        const resolvedFieldName = fieldName || 'temperatura'
        const campoMeta =
            this.state.campos.find((campo) => campo.identificacao === resolvedFieldName) || {
                identificacao: resolvedFieldName,
                descricao: resolvedFieldName,
            }
        return buildChartSeries(resolvedFieldName, campoMeta)
    }

    consumo(fieldName) {
        return buildConsumoSeries(fieldName || 'volume_hidrometro')
    }

    ensureSupervisorio() {
        const widgetIdsOf = (supervisorio) => {
            try {
                return JSON.parse(supervisorio?.widgets || '[]')
                    .map((widget) => widget.id)
                    .join(',')
            } catch (error) {
                return ''
            }
        }

        const freshSupervisorio = buildDemoSupervisorio()

        if (widgetIdsOf(this.state.supervisorio) !== widgetIdsOf(freshSupervisorio)) {
            this.state.supervisorio = freshSupervisorio
            this.persist()
        }

        return this.state.supervisorio
    }

    listSupervisorios() {
        this.ensureSupervisorio()
        return paginate([this.state.supervisorio])
    }

    updateSupervisorio(body = {}) {
        const supervisorio = this.ensureSupervisorio()
        this.state.supervisorio = {
            ...supervisorio,
            descricao: body.descricao || supervisorio.descricao,
            layout: body.layout || supervisorio.layout,
            widgets: body.widgets || supervisorio.widgets,
        }
        this.persist()
        return this.state.supervisorio
    }

    reporteSupervisorio() {
        if (!this.state.campos.length) {
            this.ensureSandboxDataset()
        }

        const identificacoes = this.state.campos.map((campo) => campo.identificacao)
        return buildSupervisorioReporte(identificacoes)
    }

    historicoSupervisorio(body = {}) {
        return buildSupervisorioHistorico(body)
    }
}

const demoStore = new DemoStore()

export { parseBody }
export default demoStore
